import React, { useEffect } from "react";
import { Pencil, Trash2, ChevronDown, RotateCcw, X, Lightbulb, FileText } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { GROEP, groepVan, relLabel, datumKort, kortDatum, EIGENAAR_LABEL, effDeadline, isIntern, internLabel, cap } from "./afsprakenUtils";
import { OpvolgerAvatar } from "./Opvolger";
import { StatusPill, KlaarLabel } from "./AfspraakRow";
import { CheckpointTimeline, LocatieMeldtDeadline } from "./CheckpointTimeline";
import { Reacties } from "./Reacties";
import { fmtReactieTijd } from "./reactieUtils";

const Fact = ({ label, children, testid }) => (
  <div className="min-w-0" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-0.5">{label}</div>
    <div className="text-sm text-zinc-800 break-words">{children}</div>
  </div>
);

const OpvolgerSelect = ({ a, oms, onChange }) => {
  const eff = a.opvolgerEffectief || "";
  const opties = eff && !oms.includes(eff) ? [...oms, eff] : oms;
  return (
    <div className="flex items-center gap-2">
      <OpvolgerAvatar name={eff} />
      <select data-testid="detail-opvolger-select" value={eff} onChange={(e) => onChange(a, e.target.value || null)}
        className={`flex-1 min-w-0 px-2 py-1 rounded border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 ${!eff || !a.opvolgerActief ? "text-amber-700 font-semibold" : "text-zinc-900"}`}>
        <option value="">Geen opvolger</option>
        {opties.map((o) => <option key={o} value={o}>{cap(o)}{o === eff && !a.opvolgerActief ? " (uit dienst)" : ""}</option>)}
      </select>
    </div>
  );
};

const VervolgLink = ({ info, onGaNaar, testid, tekst }) => (
  <button type="button" data-testid={testid} onClick={() => onGaNaar(info.id)}
    className="text-left min-h-[44px] sm:min-h-0 text-sm text-blue-700 underline underline-offset-2 hover:text-blue-900">
    {tekst}
  </button>
);

const Reflectie = ({ a }) => (
  <div className="space-y-3" data-testid={`reflectie-${a.id}`}>
    <div data-testid={`reflectie-samenvatting-${a.id}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-zinc-400"><FileText className="h-3 w-3" /> Samenvatting</div>
      <p className="text-sm text-zinc-700 whitespace-pre-line break-words">{a.samenvatting || "—"}</p>
    </div>
    <div data-testid={`reflectie-analyse-${a.id}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-amber-600"><Lightbulb className="h-3 w-3" /> Kritische analyse</div>
      <p className="text-sm text-zinc-700 whitespace-pre-line break-words">{a.analyse || "—"}</p>
    </div>
  </div>
);

const Acties = ({ a, onAfronden, onHeropenen, onEdit, onDelete }) => (
  <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-zinc-200">
    {a.status === "open" ? (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <button data-testid="detail-afronden-btn" className="flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 transition-colors">
            Afronden <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem data-testid="detail-afronden-nagekomen" onSelect={() => onAfronden(a, "nagekomen")}>Nagekomen…</DropdownMenuItem>
          <DropdownMenuItem data-testid="detail-afronden-niet" onSelect={() => onAfronden(a, "niet")}>Niet nagekomen…</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ) : (
      <button data-testid="detail-heropenen-btn" onClick={() => onHeropenen(a)}
        className="flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 transition-colors">
        <RotateCcw className="h-3.5 w-3.5" /> Heropenen
      </button>
    )}
    <button data-testid={`edit-afspraak-${a.id}`} onClick={() => onEdit(a)}
      className="flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-zinc-300 bg-white text-zinc-800 text-xs font-semibold hover:border-zinc-500 transition-colors">
      <Pencil className="h-3.5 w-3.5" /> Bewerken
    </button>
    <button data-testid={`delete-afspraak-${a.id}`} onClick={() => onDelete(a)} aria-label="Verwijderen"
      className="h-9 w-9 rounded-md border border-rose-200 flex items-center justify-center text-rose-600 hover:border-rose-300 hover:bg-rose-50 transition-colors">
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export const AfspraakDetail = ({ a, oms, me, onClose, onOpvolger, onToggleCp, onNoteChange, onNoteFlush, onReacties, onTerugzetten, onGaNaar, ...acties }) => {
  // Wegklikken/sluiten/tab wisselen mag nooit een getypte, onopgeslagen notitie verliezen.
  useEffect(() => () => onNoteFlush(a.id), [a.id, onNoteFlush]);
  const open = a.status === "open";
  const g = GROEP[groepVan(a)];
  const heeftCps = (a.checkpoints || []).length > 0;
  const weg = !!a.verwijderd;
  return (
    <div className="p-5 space-y-5" data-testid="afspraak-detail">
      <div className="flex items-start gap-3">
        {open ? <span className={`mt-2 h-3 w-3 rounded-full shrink-0 ${g.dot}`} /> : <StatusPill status={a.status} />}
        <h2 data-testid="detail-titel" className="flex-1 font-heading font-black text-lg leading-snug text-zinc-900 whitespace-pre-line break-words">{a.wat}</h2>
        {a.bron === "locatie" && <span data-testid="detail-door-locatie" className="mt-1 shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-700">door de locatie</span>}
        {onClose && (
          <button data-testid="detail-sluiten" onClick={onClose} aria-label="Sluiten" className="h-8 w-8 -mr-2 -mt-1 rounded-md flex items-center justify-center text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {weg && (
        <p data-testid="detail-verwijderd" className="text-sm text-zinc-700 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
          Verwijderd door <span className="capitalize font-semibold">{a.verwijderd.door}</span> op {fmtReactieTijd(a.verwijderd.op)}. Alleen lezen: zet hem terug om hem weer te gebruiken.
        </p>
      )}
      <fieldset disabled={weg} className="min-w-0 border-0 p-0 m-0">
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label={isIntern(a) ? "Soort" : "Locatie"} testid="detail-locatie">{isIntern(a) ? `Intern — alleen betrokkenen en oversight${a.locatie ? ` · over ${a.locatie}` : ""}` : a.locatie || "Geen / algemeen"}</Fact>
        <Fact label="Deadline" testid="detail-deadline">
          <span className={`font-mono ${open && a.overdue ? "text-rose-600 font-bold" : ""}`}>{datumKort(effDeadline(a)) || "—"}</span>
          {open && a.klaarOmAfTeRonden ? <span className="block mt-1"><KlaarLabel testid="detail-klaar" /></span>
            : open && a.wachtOpOm ? <span data-testid="detail-wacht-op-om" className="block text-xs text-blue-700 font-semibold">{a.wachtOpOmLabel}</span>
            : open && effDeadline(a) && <span className={`block text-xs ${a.overdue ? "text-rose-600 font-semibold" : "text-zinc-500"}`}>{relLabel(a)}</span>}
        </Fact>
        {a.blReported?.deadline && <div className="col-span-2"><LocatieMeldtDeadline a={a} /></div>}
        {isIntern(a) ? (
          <div className="col-span-2"><Fact label="Betrokkenen" testid="detail-intern">{internLabel(a)}</Fact></div>
        ) : (<>
          <div className="col-span-2"><Fact label="Opvolger"><OpvolgerSelect a={a} oms={oms} onChange={onOpvolger} /></Fact></div>
          <Fact label="Eigenaar">{a.eigenaar === "afdeling" ? <span data-testid="detail-afdeling">Afdeling: {a.afdelingNaam || a.afdeling}</span> : EIGENAAR_LABEL[a.eigenaar] || a.eigenaar || "—"}</Fact>
          {a.werkbon && <Fact label="Werkbon"><span data-testid="detail-werkbon" className="font-mono">{a.werkbon}</span></Fact>}
          {a.afdelingReported && Object.entries(a.afdelingReported).map(([k, v]) => v?.note || v?.reported ? (
            <Fact key={k} label={k === "deadline" ? "Afdeling meldt (deadline)" : "Afdeling meldt"}><span data-testid={`detail-afdeling-melding-${k}`}>{v.note || ""}{v.reported ? " · gereed" : ""}</span></Fact>) : null)}
          <Fact label="Bedrijfsleider">{a.bedrijfsleider || "—"}</Fact>
        </>)}
        <Fact label="Aangemaakt" testid={`afspraak-aangemaakt-${a.id}`}><span className="font-mono">{datumKort(a.timestamp) || "—"}</span> <span className="text-zinc-500">door {a.door}</span></Fact>
        {a.vervolgInfo && <div className="col-span-2" data-testid="detail-vervolg"><VervolgLink info={a.vervolgInfo} onGaNaar={onGaNaar} testid="detail-vervolg-link" tekst={`Vervolg: nieuwe afspraak met deadline ${kortDatum(a.vervolgInfo.deadline) || "onbekend"}`} /></div>}
        {a.vervolgOpInfo && <div className="col-span-2" data-testid="detail-vervolg-op"><VervolgLink info={a.vervolgOpInfo} onGaNaar={onGaNaar} testid="detail-vervolg-op-link" tekst={`Vervolg op een afspraak die niet is nagekomen (deadline ${kortDatum(a.vervolgOpInfo.deadline) || "onbekend"})`} /></div>}
        {!open && <Fact label="Afgerond op" testid="detail-afgerondop"><span className="font-mono">{datumKort(a.afgerondOp) || "—"}</span></Fact>}
      </div>
      </fieldset>
      {heeftCps && (
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-2">Voortgang</div>
          <fieldset disabled={weg} className="min-w-0 border-0 p-0 m-0"><CheckpointTimeline a={a} onToggle={onToggleCp} onNoteChange={onNoteChange} onNoteBlur={onNoteFlush} /></fieldset>
        </div>
      )}
      {!open && <Reflectie a={a} />}
      <Reacties key={a.id} a={a} me={me} onReacties={onReacties} leesalleen={weg} />
      {weg ? (
        <div className="pt-4 border-t border-zinc-200">
          <button data-testid="detail-terugzetten-btn" onClick={() => onTerugzetten(a)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 transition-colors">
            <RotateCcw className="h-3.5 w-3.5" /> Terugzetten
          </button>
        </div>
      ) : <Acties a={a} {...acties} />}
    </div>
  );
};
